import { useGetAllShopEmailTemplates } from "@babackapp/private-api-client/client/use-get-all-shop-email-templates";
import { Button } from "@babackapp/ui/components/button";
import { useTranslation } from "react-i18next";
import { Position } from "@xyflow/react";
import { ExternalLink, Mail, Paintbrush } from "lucide-react";
import useCurrentShopSuspense from "../../hooks/use-current-shop-suspense";
import { useWorkflowsEditorRouting } from "../../context/workflows-editor-routing-context";
import { BaseHandle } from "../../components/base-handle";
import { BaseNode, BaseNodeContent, BaseNodeFooter } from "../../components/base-node";
import { EmailActionNodeProps } from "./types";
import { useNodeData } from "../../hooks/use-node-data";
import {
  ActionNodeDataDto,
  ActionNodeEmailConfigDto,
} from "@babackapp/private-api-client/types";
import { useIsWorkflowsListingMode } from "../../context/workflows-editor-workflows-context";
import { ContextualTipsTrigger } from "@babackapp/ui/components/contextual-tips";


export const EMAIL_TEMPLATE_NODE_WIDTH = 280;
export const EMAIL_TEMPLATE_NODE_HEIGHT_ESTIMATION = 160;

export function EmailActionNodeEditor({ id, data }: EmailActionNodeProps) {
  const { t } = useTranslation("trackoffice");
  const { data: shop } = useCurrentShopSuspense();
  const { openTemplate } = useWorkflowsEditorRouting();
  const { data: templates = [] } = useGetAllShopEmailTemplates(shop.id);
  const isWorkflowsListingMode = useIsWorkflowsListingMode();

  const liveData = useNodeData<ActionNodeDataDto>(id);
  const templateId =
    liveData?.actionType === "send_tracking_email" &&
    "templateId" in liveData.config
      ? liveData.config.templateId
      : data.templateId;
  const recipient =
    (liveData?.actionType === "send_tracking_email"
      ? (liveData.config as ActionNodeEmailConfigDto).recipient
      : data.recipient) ?? "customer";
  const hasOverrides =
    liveData?.actionType === "send_tracking_email" &&
    "overrides" in liveData.config
      ? !!liveData.config.overrides
      : !!data.overrides;
  const isDefaultTemplate = !templateId || templateId === "null";

  const handleCustomizeClick = () => {
    if (!isDefaultTemplate && templateId) {
      openTemplate?.(templateId);
    }
  };

  const selectedTemplate = templates?.find(
    (template) => template.id === templateId,
  );

  return (
    <ContextualTipsTrigger
      tips={[
        {
          id: "workflow-node-email",
          Icon: Mail,
          title: t(
            "workflowsEditor.tips.workflowNodeEmail.title",
            "This is an email action node",
          ),
          description: t(
            "workflowsEditor.tips.workflowNodeEmail.desc",
            "This node will send an email to the customer using a specific template. You can customize the template using the button on the node's card.",
          ),
        },
      ]}
    >
      <BaseNode nodeId={id} style={{ position: "relative" }}>
        <BaseNodeContent>
          <div className="flex flex-col gap-2 w-full">
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                Template
                {hasOverrides && (
                  <Paintbrush className="size-3 text-violet-500" />
                )}
              </span>
              <span className="text-xs mt-1 font-semibold">
                {selectedTemplate?.title ?? "Default"}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">Recipient</span>
              <span className="text-xs mt-1 font-semibold">
                {t(`workflowsEditor.panels.emailRecipient.options.${recipient}`)}
              </span>
            </div>
          </div>
          <BaseHandle type="target" position={Position.Left} />
          <BaseHandle
            type="source"
            position={Position.Right}
            style={{ top: "50%", transform: "translateY(-50%)" }}
          />
        </BaseNodeContent>
        {!isDefaultTemplate && !isWorkflowsListingMode && (
          <BaseNodeFooter>
            <div className="justify-end items-end w-full flex">
              <Button
                size="icon-xs"
                variant="ghost"
                className="nodrag shrink-0"
                onClick={handleCustomizeClick}
                title={t(
                  "workflowsEditor.emailNode.customizeTemplate",
                  "Customize template",
                )}
              >
                <ExternalLink className="size-3" />
              </Button>
            </div>
          </BaseNodeFooter>
        )}
      </BaseNode>
    </ContextualTipsTrigger>
  );
}
